<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-semibold text-gray-800">Outpatient Summary</h2>
      <div class="flex items-center gap-3">
        <select v-model="careSetting" class="px-2 py-1.5 text-sm border border-gray-300 rounded bg-white" @change="loadData">
          <option value="">All Settings</option>
          <option value="SNF">SNF</option>
          <option value="ALF">ALF</option>
        </select>
        <label class="text-xs text-gray-500">From</label>
        <input type="date" v-model="clmFromDt" class="px-2 py-1.5 text-sm border border-gray-300 rounded bg-white" @change="loadData" />
        <label class="text-xs text-gray-500">To</label>
        <input type="date" v-model="clmThruDt" class="px-2 py-1.5 text-sm border border-gray-300 rounded bg-white" @change="loadData" />
        <div class="inline-flex rounded border border-gray-300 overflow-hidden text-sm">
          <button
            class="px-3 py-1.5"
            :class="view === 'category' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'"
            @click="view = 'category'"
          >By Category</button>
          <button
            class="px-3 py-1.5 border-l border-gray-300"
            :class="view === 'facility' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'"
            @click="view = 'facility'"
          >By Facility</button>
        </div>
        <span v-if="view === 'facility' && filterCategory" class="text-sm text-gray-500 flex items-center gap-1">
          <button class="text-blue-600 hover:underline" @click="view = 'category'">Categories</button>
          <span>›</span>
          <span class="text-gray-800 font-medium">{{ filterCategory }}</span>
        </span>
        <ExportMenu :exports="[{ type: 'csv', action: exportCsv }]" />
      </div>
    </div>
    <!-- Filters -->
    <div class="flex items-center gap-3 mb-3">
      <select v-model="filterCategory" class="px-2 py-1.5 text-sm border border-gray-300 rounded bg-white" style="max-width:320px;">
        <option value="">All Categories</option>
        <option v-for="c in distinctCategories" :key="c" :value="c">{{ c }}</option>
      </select>
      <select v-if="view === 'facility'" v-model="filterFacility" class="px-2 py-1.5 text-sm border border-gray-300 rounded bg-white" style="max-width:320px;">
        <option value="">All Facilities</option>
        <option v-for="f in distinctFacilities" :key="f" :value="f">{{ f }}</option>
      </select>
    </div>
    <p class="text-xs text-gray-400 mb-3 leading-relaxed">
      Outpatient cost by revenue code category from CCLF Part A claims (type 40/41/42).
      Patient panel based on CMS attribution report as of Dec 1, {{ performanceYear - 1 }}.
      Claims maturity filtered at 3 months post-year-end (EFCTV ≤ Mar 27, {{ performanceYear }}).
      PBPM = category total cost ÷ patients with claims in that category.
    </p>

    <LoadingIndicator v-if="loading" message="Loading outpatient data…" />

    <div v-else-if="activeRowData.length" class="ext-table-wrap" style="overflow-x:auto;">
      <table class="ext-table" style="min-width:800px;">
        <thead>
          <tr>
            <th v-for="col in activeColumns" :key="col.field"
                :class="col.numeric ? 'num' : ''"
                :style="!col.numeric ? 'padding-left:16px; min-width:' + (col.width || 240) + 'px; cursor:pointer;' : 'cursor:pointer; min-width:' + (col.width || 120) + 'px;'"
                @click="toggleSort(col.field)">
              {{ col.header }}
              <span v-if="sortField === col.field" style="margin-left:2px; font-size:8px;">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
            </th>
          </tr>
          <!-- Totals row -->
          <tr class="totals-row">
            <td :style="'padding-left:16px; font-weight:600; color:#221F1C;'" :colspan="view === 'facility' ? 2 : 1">Total</td>
            <td v-if="view === 'facility'" />
            <td class="num">{{ fmtCurrency(totals.total_cost) }}</td>
            <td class="num sensitive">{{ fmtInt(totals.patient_count) }}</td>
            <td class="num">{{ fmtCurrency(totals.pbpm) }}</td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, ri) in sortedRows" :key="ri"
              class="cursor-pointer"
              :class="isSelected(row) ? 'bg-blue-50' : 'hover:bg-gray-50'"
              @click="selectRow(row)">
            <td class="metric-name sensitive" v-if="view === 'facility'">{{ row.facility }}</td>
            <td :class="view === 'facility' ? '' : 'metric-name'">{{ row.category }}</td>
            <td class="num" :style="heatStyle(row.total_cost)">{{ fmtCurrency(row.total_cost) }}</td>
            <td class="num sensitive">{{ fmtInt(row.patient_count) }}</td>
            <td class="num">{{ fmtCurrency(row.pbpm) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Patient drill-down -->
    <div v-if="selectedRow" class="mt-4">
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center gap-2">
          <span class="text-sm font-semibold text-gray-800">
            Patients: {{ selectedRow.category }}
            <span class="sensitive" v-if="selectedRow.facility"> · {{ selectedRow.facility }}</span>
          </span>
          <button @click="selectedRow = null; drilldownRows = []" class="text-xs text-gray-400 hover:text-gray-600">Clear</button>
        </div>
        <ExportMenu :disabled="!drilldownRows.length" :exports="[{ type: 'csv', action: exportDrilldownCsv }]" />
      </div>
      <LoadingIndicator v-if="drilldownLoading" message="Loading patients…" />
      <div v-else-if="drilldownRows.length" class="ext-table-wrap" style="overflow-x:auto; max-height:400px; overflow-y:auto;">
        <table class="ext-table" style="min-width:700px;">
          <thead>
            <tr>
              <th style="padding-left:16px; min-width:140px;">Patient</th>
              <th style="min-width:120px;">MBI</th>
              <th v-if="drilldownRows[0].facility_name" style="min-width:180px;">Facility</th>
              <th style="min-width:100px;">Category</th>
              <th class="num" style="min-width:110px;">Total Cost</th>
              <th class="num" style="min-width:80px;">Claims</th>
              <th style="min-width:100px;">First Service</th>
              <th style="min-width:100px;">Last Service</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(pt, pi) in drilldownRows" :key="pi">
              <td class="sensitive" style="padding-left:16px;">{{ pt.bene_first_name }} {{ pt.bene_last_name }}</td>
              <td class="font-mono text-gray-400 sensitive" style="font-size:11px;">{{ pt.bene_mbi_id }}</td>
              <td class="sensitive" v-if="drilldownRows[0].facility_name">{{ pt.facility_name || '—' }}</td>
              <td>{{ pt.category }}</td>
              <td class="num">{{ fmtCurrency(pt.total_cost) }}</td>
              <td class="num">{{ fmtInt(pt.claim_count) }}</td>
              <td>{{ pt.first_service || '—' }}</td>
              <td>{{ pt.last_service || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="text-xs text-gray-400 py-4 text-center">No patient data found.</div>
    </div>

    <div v-else-if="!loading && !activeRowData.length" class="flex items-center justify-center py-20" style="color:#A5A5A6; font-size:13px;">
      No data available
    </div>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'

const CATEGORY_COLUMNS = [
  { field: 'category', header: 'Category', width: 320 },
  { field: 'total_cost', header: 'Total Cost', width: 150, numeric: true },
  { field: 'patient_count', header: 'Patients', width: 110, numeric: true },
  { field: 'pbpm', header: 'PBPM', width: 120, numeric: true },
]

const FACILITY_COLUMNS = [
  { field: 'facility', header: 'Facility', width: 240 },
  { field: 'category', header: 'Category', width: 280 },
  { field: 'total_cost', header: 'Total Cost', width: 150, numeric: true },
  { field: 'patient_count', header: 'Patients', width: 110, numeric: true },
  { field: 'pbpm', header: 'PBPM', width: 120, numeric: true },
]

export default {
  name: 'CCLFReport_OutpatientSummary',
  components: { LoadingIndicator, ExportMenu },
  props: {
    contractId: { type: String, default: null },
    performanceYear: { type: Number, default: null },
    dosthru: { type: String, default: null },
    entityId: { type: Number, default: null },
  },
  data() {
    return {
      careSetting: '',
      byCategory: [],
      byFacility: [],
      loading: false,
      view: 'category',
      clmFromDt: this.performanceYear ? `${this.performanceYear}-01-01` : null,
      clmThruDt: new Date().toISOString().slice(0, 10),
      sortField: 'total_cost',
      sortDir: 'desc',
      filterCategory: '',
      filterFacility: '',
      selectedRow: null,
      drilldownRows: [],
      drilldownLoading: false,
    }
  },
  computed: {
    activeColumns() {
      return this.view === 'facility' ? FACILITY_COLUMNS : CATEGORY_COLUMNS
    },
    activeRowData() {
      let rows = this.view === 'facility' ? this.byFacility : this.byCategory
      if (this.filterCategory) {
        rows = rows.filter(r => r.category === this.filterCategory)
      }
      if (this.view === 'facility' && this.filterFacility) {
        rows = rows.filter(r => r.facility === this.filterFacility)
      }
      return rows
    },
    distinctCategories() {
      const src = this.view === 'facility' ? this.byFacility : this.byCategory
      return [...new Set(src.map(r => r.category).filter(Boolean))].sort()
    },
    distinctFacilities() {
      return [...new Set(this.byFacility.map(r => r.facility).filter(Boolean))].sort()
    },
    sortedRows() {
      const rows = [...this.activeRowData]
      const f = this.sortField
      const dir = this.sortDir === 'asc' ? 1 : -1
      if (f === 'category' || f === 'facility') {
        rows.sort((a, b) => (a[f] || '').localeCompare(b[f] || '') * dir)
      } else {
        rows.sort((a, b) => ((a[f] || 0) - (b[f] || 0)) * dir)
      }
      return rows
    },
    totals() {
      const data = this.activeRowData
      if (!data.length) return {}
      const totalCost = data.reduce((s, r) => s + (Number(r.total_cost) || 0), 0)
      const totalPatients = data.reduce((s, r) => s + (Number(r.patient_count) || 0), 0)
      return {
        total_cost: totalCost,
        patient_count: totalPatients,
        pbpm: totalPatients > 0 ? Math.round(totalCost / totalPatients) : null,
      }
    },
    costMax() {
      return Math.max(0, ...this.activeRowData.map(r => Number(r.total_cost) || 0))
    },
  },
  watch: {

    contractId() { this.loadData() },
    performanceYear() { this.loadData() },
    dosthru() { this.loadData() },
    entityId() { this.loadData() },
    view() { this.filterCategory = ''; this.filterFacility = ''; this.selectedRow = null; this.drilldownRows = [] },
  },
  mounted() {
    if (this.contractId && this.performanceYear) this.loadData()
  },
  methods: {
    toggleSort(field) {
      if (this.sortField === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortDir = (field === 'category' || field === 'facility') ? 'asc' : 'desc'
      }
    },
    async loadData() {
      if (!this.contractId || !this.performanceYear) return
      this.loading = true
      try {
        const payload = {
          contract_id: this.contractId,
          performance_year: this.performanceYear,
          dosthru: this.dosthru,
          entity_id: this.entityId,
          care_setting: this.careSetting || undefined,
        }
        if (this.clmFromDt) payload.clm_from_dt = this.clmFromDt
        if (this.clmThruDt) payload.clm_thru_dt = this.clmThruDt
        const response = await this.$axios.post('/api/portfolio_management/cclf-report/outpatient-summary/', payload)
        this.byCategory = response.data.by_category || []
        this.byFacility = response.data.by_facility || []
      } catch (error) {
        console.error('Failed to load outpatient summary:', error)
      } finally {
        this.loading = false
      }
    },
    fmtCurrency(val) {
      if (val == null) return '—'
      return '$' + Math.round(val).toLocaleString('en-US')
    },
    fmtInt(val) {
      if (val == null) return '—'
      return Math.round(val).toLocaleString('en-US')
    },
    heatStyle(val) {
      if (val == null || val <= 0 || !this.costMax) return null
      const ratio = Math.min(val / this.costMax, 1)
      return { backgroundColor: `rgba(37, 99, 235, ${ratio * 0.45})` }
    },
    isSelected(row) {
      if (!this.selectedRow) return false
      return row.category === this.selectedRow.category && row.facility === this.selectedRow.facility
    },
    async selectRow(row) {
      if (this.isSelected(row)) {
        this.selectedRow = null
        this.drilldownRows = []
        return
      }
      // Category view: clicking a category drills into facility view for that category
      if (this.view === 'category') {
        this.filterCategory = row.category
        this.view = 'facility'
        return
      }
      this.selectedRow = row
      this.drilldownLoading = true
      this.drilldownRows = []
      try {
        const payload = {
          contract_id: this.contractId,
          performance_year: this.performanceYear,
          entity_id: this.entityId,
          category: row.category,
          care_setting: this.careSetting || undefined,
        }
        if (this.clmFromDt) payload.clm_from_dt = this.clmFromDt
        if (this.clmThruDt) payload.clm_thru_dt = this.clmThruDt
        if (row.facility) payload.facility_name = row.facility
        const { data } = await this.$axios.post('/api/portfolio_management/cclf-report/outpatient-patients/', payload)
        this.drilldownRows = data || []
      } catch (e) {
        console.error('Failed to load outpatient patients:', e)
      } finally {
        this.drilldownLoading = false
      }
    },
    exportDrilldownCsv() {
      const data = this.drilldownRows
      if (!data.length) return
      const cols = ['bene_first_name', 'bene_last_name', 'bene_mbi_id', 'facility_name', 'category', 'total_cost', 'claim_count', 'first_service', 'last_service']
      const header = ['First Name', 'Last Name', 'MBI', 'Facility', 'Category', 'Total Cost', 'Claims', 'First Service', 'Last Service'].join(',')
      const lines = data.map(r => cols.map(c => {
        const v = r[c]
        if (v == null) return ''
        if (typeof v === 'string') return `"${v.replace(/"/g, '""')}"`
        return v
      }).join(','))
      const csv = [header, ...lines].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = 'outpatient_patients.csv'
      a.click()
    },
    exportCsv() {
      const data = this.sortedRows
      if (!data.length) return
      const cols = this.activeColumns
      const header = cols.map(c => c.header).join(',')
      const lines = data.map(r =>
        cols.map(c => {
          const v = r[c.field]
          if (v == null) return ''
          if (c.field === 'category' || c.field === 'facility') return `"${String(v).replace(/"/g, '""')}"`
          return v
        }).join(',')
      )
      const csv = [header, ...lines].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `cclf_outpatient_${this.view}.csv`
      a.click()
      URL.revokeObjectURL(url)
    },
  },
}
</script>

<style scoped>
.totals-row td {
  font-family: 'JetBrains Mono', 'SF Mono', ui-monospace, monospace;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum", "zero";
  font-size: 11.5px;
  font-weight: 600;
  color: #221F1C;
  padding: 8px 10px;
  border-bottom: 2px solid #E5E0D9;
  background: #FAF9F6;
  text-align: right;
}
</style>
